/* Infinote announcement bar (overlay on Glozin's announcement-bar section). Two motions, chosen in the section setting
   "Infinote message motion" (data-infinote-motion on <announcement-bar>):
   - fade (default): one message at a time, slow cross-fade, long hold. Real text, no live region (no repeated announcements).
   - typewriter: types and deletes each message (ported from the InfinoteShopifyStores theme).
   Reads the section's own blocks, so copy is edited in header-group.json / the theme editor. Keeps links.
   Both pause on hover, keyboard focus and hidden tabs. prefers-reduced-motion: messages swap without animation. */
(() => {
  const FADE = { HOLD_MS: 5200, FADE_MS: 700, GAP_MS: 250 };
  const TYPE = { TYPE_MS: 62, DELETE_MS: 34, HOLD_MS: 1650, GAP_MS: 350 };
  const STATIC_ROTATE_MS = 6000;
  const reduced = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const collect = (bar) => {
    const seen = new Set(), list = [];
    bar.querySelectorAll('.announcement__bar-message .grow').forEach((node) => {
      const text = (node.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text || seen.has(text)) return;
      seen.add(text);
      const a = node.querySelector('a[href]');
      list.push({ text, href: a ? a.getAttribute('href') : null });
    });
    return list;
  };

  const pausable = (root) => {
    const s = { paused: false };
    ['mouseenter', 'focusin'].forEach((e) => root.addEventListener(e, () => { s.paused = true; }));
    ['mouseleave', 'focusout'].forEach((e) => root.addEventListener(e, () => { s.paused = false; }));
    return s;
  };
  const busy = (s) => s.paused || document.hidden;

  function fade(holder, messages, longest) {
    holder.innerHTML =
      '<div class="infinote-tw infinote-tw--fade">' +
        '<span class="infinote-tw__sizer" aria-hidden="true"></span>' +
        '<a class="infinote-tw__line"><span class="infinote-tw__text"></span></a>' +
      '</div>';
    const root = holder.firstElementChild;
    root.querySelector('.infinote-tw__sizer').textContent = longest;
    const line = root.querySelector('.infinote-tw__line'), out = root.querySelector('.infinote-tw__text');
    const show = (m) => {
      out.textContent = m.text;
      if (m.href) { line.setAttribute('href', m.href); }
      else { line.removeAttribute('href'); }
    };
    const s = pausable(root);
    let i = 0;
    show(messages[0]);
    if (messages.length < 2) return;
    const still = reduced();
    const next = () => {
      if (busy(s)) return window.setTimeout(next, 400);
      if (still) { i = (i + 1) % messages.length; show(messages[i]); return window.setTimeout(next, STATIC_ROTATE_MS); }
      line.classList.add('is-out');                                   // fade out, swap while invisible, fade in
      window.setTimeout(() => {
        i = (i + 1) % messages.length; show(messages[i]);
        window.setTimeout(() => { line.classList.remove('is-out'); window.setTimeout(next, FADE.HOLD_MS + FADE.FADE_MS); }, FADE.GAP_MS);
      }, FADE.FADE_MS);
    };
    window.setTimeout(next, still ? STATIC_ROTATE_MS : FADE.HOLD_MS);
  }

  function typewriter(holder, messages, longest) {
    holder.innerHTML =
      '<div class="infinote-tw" role="status">' +
        '<span class="infinote-tw__sizer" aria-hidden="true"></span>' +
        '<a class="infinote-tw__line" aria-hidden="true"><span class="infinote-tw__text"></span><span class="infinote-tw__cursor"></span></a>' +
      '</div>';
    const root = holder.firstElementChild;
    root.querySelector('.infinote-tw__sizer').textContent = longest;
    const line = root.querySelector('.infinote-tw__line'), out = root.querySelector('.infinote-tw__text');
    const setMessage = (m) => {
      root.setAttribute('aria-label', m.text);
      if (m.href) line.setAttribute('href', m.href); else line.removeAttribute('href');
    };
    const s = pausable(root);
    let i = 0, n = 0, deleting = false;
    const later = (fn, ms) => window.setTimeout(fn, ms);
    const tick = () => {
      if (busy(s)) return later(tick, 300);
      const m = messages[i]; setMessage(m);
      if (!deleting) {
        n += 1; out.textContent = m.text.slice(0, n);
        if (n >= m.text.length) { deleting = true; return later(tick, TYPE.HOLD_MS); }
        return later(tick, TYPE.TYPE_MS);
      }
      n -= 1; out.textContent = m.text.slice(0, Math.max(n, 0));
      if (n <= 0) { deleting = false; i = (i + 1) % messages.length; return later(tick, TYPE.GAP_MS); }
      later(tick, TYPE.DELETE_MS);
    };
    const rotate = () => {
      if (!busy(s)) i = (i + 1) % messages.length;
      const m = messages[i]; setMessage(m); out.textContent = m.text;
      later(rotate, STATIC_ROTATE_MS);
    };
    if (reduced()) { root.classList.add('is-static'); setMessage(messages[0]); out.textContent = messages[0].text; if (messages.length > 1) later(rotate, STATIC_ROTATE_MS); }
    else tick();
  }

  const init = () => {
    document.querySelectorAll('announcement-bar').forEach((bar) => {
      if (!bar.querySelector('.announcement-bar-close')) {   // no close button = always shown; undo an old stored dismissal
        try { sessionStorage.removeItem('announcement_bar'); } catch (e) {}
        const wrap = bar.closest('.shopify-section');
        if (wrap) wrap.classList.remove('hidden');
      }
      if (bar.dataset.infinoteTypewriter === 'on') return;
      const messages = collect(bar);
      const holder = bar.querySelector(':scope > div');
      if (!messages.length || !holder) return;
      bar.dataset.infinoteTypewriter = 'on';
      bar.classList.add('infinote-typewriter-ready');
      const longest = messages.reduce((a, m) => (m.text.length > a.length ? m.text : a), '');
      (bar.dataset.infinoteMotion === 'typewriter' ? typewriter : fade)(holder, messages, longest);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
